import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PortalShell from '../components/PortalShell';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import { apiClient, ApiError } from '../utils/apiClient';
import { cleanEmailText } from '../utils/emailText';
import EmailText from '../components/EmailText';
import ActivityFiles from '../components/ActivityFiles';
import ReplyFiles from '../components/ReplyFiles';
import { addReplyFiles, filesFromPaste, uploadTicketFiles } from '../utils/ticketFiles';
import './PortalTicketDetail.css';

function PortalTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [replyError, setReplyError] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/api/tickets/mine/${id}`);
      setTicket(data.ticket);
      setActivities(data.activities);
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError(err instanceof ApiError && err.status === 404 ? 'Ticket not found.' : 'Failed to load this ticket.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTicket(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    setReplyError('');
    try {
      const attachment_ids = replyFiles.length > 0 ? await uploadTicketFiles(ticket.ticket_id, replyFiles) : undefined;
      await apiClient.post(`/api/tickets/mine/${id}/reply`, { description: reply, ...(attachment_ids ? { attachment_ids } : {}) });
      setReplyFiles([]);
      setReply('');
      await fetchTicket();
    } catch (err) {
      console.error('Error sending reply:', err);
      setReplyError(err instanceof ApiError && err.message ? err.message : 'We couldn’t send your reply. Your message is still here - please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) return <PortalShell><p aria-live="polite">Loading ticket&hellip;</p></PortalShell>;
  if (error || !ticket) return <PortalShell><p>{error || 'Ticket not found.'}</p></PortalShell>;

  return (
    <PortalShell>
      <button type="button" className="portal-back-link" onClick={() => navigate('/portal/tickets')}>&larr; Back to Support</button>
      <h1>{ticket.subject}</h1>
      <p className="portal-ticket-meta">#{ticket.ticket_number} &middot; opened {new Date(ticket.created_at).toLocaleDateString()}</p>

      <div className="portal-thread">
        {ticket.description && (
          <div className="portal-thread-item portal-thread-item-you">
            <div className="portal-thread-author">You</div>
            <div className="portal-thread-body"><EmailText text={cleanEmailText(ticket.description).full} /></div>
          </div>
        )}
        {activities.map((activity) => (
          <div
            key={activity.id}
            className={`portal-thread-item ${activity.author_user_id === Globals.Id ? 'portal-thread-item-you' : ''}`}
          >
            <div className="portal-thread-author">{activity.author_user_id === Globals.Id ? 'You' : 'Support Team'}</div>
            <div className="portal-thread-body"><EmailText text={cleanEmailText(activity.description).reply} /></div>
            <ActivityFiles files={activity.additional_data?.files} />
            <div className="portal-thread-date">{new Date(activity.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>

      {ticket.status !== 'closed' && (
        <form className="portal-reply-form" onSubmit={submitReply}>
          <textarea
            rows={4}
            aria-label="Your reply"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Write a reply…"
            onPaste={(e) => {
              const pasted = filesFromPaste(e);
              if (pasted.length === 0) return;
              e.preventDefault();
              const result = addReplyFiles(replyFiles, pasted);
              setReplyFiles(result.files);
              if (result.error) setReplyError(result.error);
            }}
          />
          <ReplyFiles
            files={replyFiles}
            disabled={sending}
            onAdd={(incoming) => {
              const result = addReplyFiles(replyFiles, incoming);
              setReplyFiles(result.files);
              setReplyError(result.error);
            }}
            onRemove={(i) => setReplyFiles((files) => files.filter((_, idx) => idx !== i))}
          />
          {replyError && <p className="portal-reply-error" role="alert">{replyError}</p>}
          <Button type="submit" disabled={sending || !reply.trim()}>{sending ? 'Sending…' : 'Send Reply'}</Button>
        </form>
      )}
    </PortalShell>
  );
}

export default PortalTicketDetail;
